<template>
    <ui-component-modal
      :modalTitle="
        $t(`Components.Yield.ModalEditYieldPrice.Title_EditPriceStructure`)
      "
      :isSaving="isSaving"
      :isSavingSuccess="isSavingSuccess"
      :isSavingError="isSavingError"
      :onClickCancel="onClickCancel"
      :onClickSave="saveYieldSettings"
      :hideSaveButton="isSavingError || isSavingSuccess"
      modalSize="large"
      :buttonLabelCancel="
        isSavingError || isSavingSuccess
          ? 'Close'
          : $t(`Components.Carts.EditCartData.Button_Cancel`)
      "
      :showModal="showModal"
      @closeModal="onClickCancel"
    >
      <template slot="modalTitle"></template>
      <template v-slot:content>
        <div v-if="channel && mYieldSettings">
          <div>
            <Message class="has-margin-bottom">
              <div v-html="
                $t(
                  'Components.Yield.ModalCreateYieldSettings.Message_InfoFirstRunEdit',
                  { br: '<br>' }
                )
              "></div>
          </Message>
          </div>
  
          <table class="table-settings table is-fullwidth is-striped">
            <tbody>
              <tr v-if="mYieldSettings.VoucherId > 0">
                <td>Voucher</td>
                <td>{{ mYieldSettings.VoucherName }}</td>
              </tr>
              <tr
                v-if="mYieldSettings.VoucherId === 0"
                class="has-text-vertical-middle"
              >
                <td>{{ $t('General.Channel') }}</td>
                <td>
                  <div v-if="channelId > 0">{{ mYieldSettings.ChannelName }}</div>
                </td>
              </tr>
              <tr
                v-if="mYieldSettings.VoucherId === 0"
                class="has-text-vertical-middle"
              >
                <td>
                  {{
                    $t(
                      'Components.Yield.ModalCreateYieldSettings.Label_MeetingType'
                    )
                  }}
                </td>
                <td>
                  {{ mYieldSettings.MeetingtypeId | getMeetingtypeName }}
                </td>
              </tr>
              <tr
                v-if="voucherId === 0"
                class="has-text-vertical-middle"
              >
                <td>
                  {{
                    $t(
                      'Components.Yield.ModalCreateYieldSettings.Label_Space'
                    )
                  }}
                </td>
                <td v-if="mYieldSettings.SpaceId === 0">
                  {{
                    $t(
                      'Components.Yield.ModalCreateYieldSettings.Label_AllSpaces'
                    )
                  }}
                </td>
                <td v-if="mYieldSettings.SpaceId > 0">
                  {{ mYieldSettings.SpaceName }}
                </td>
              </tr>
              <tr
                v-if="showValidFromRow"
                class="has-text-vertical-middle"
              >
                <td>
                  {{
                    $t(`Components.Yield.ModalCreateYieldSettings.Label_ValidFrom`)
                  }}
                </td>
                <td>
                  <BaseDateField
                    v-model="startDate"
                    :openCalendarInModal="true"
                    class="customWidth"
                  />
                </td>
              </tr>
              <tr
                v-if="showStatusRow"
                class="has-text-vertical-middle"
              >
                <td>{{ $t('Prices.Status') }}</td>
                <td>
                  <div class="select">
                    <select v-model="mYieldSettings.Status">
                      <option
                        v-for="status in statusTypes"
                        :key="status"
                        :value="status"
                        :selected="mYieldSettings.Status === status"
                      >
                        {{ $t(`General.Label_${status}`) }}
                      </option>
                    </select>
                  </div>
                </td>
              </tr>
              <tr class="has-text-vertical-middle">
                <td>
                  {{
                    $t(`Components.Yield.ModalCreateYieldSettings.Label_VatRate`)
                  }}
                </td>
                <td>
                  <div class="select">
                    <select v-model="mYieldSettings.TaxPercentage">
                      <option
                        v-for="taxPercentage in taxPercentages"
                        :key="taxPercentage"
                        :value="taxPercentage"
                        :selected="taxPercentage === mYieldSettings.TaxPercentage"
                      >
                        {{ taxPercentage }}%
                      </option>
                    </select>
                  </div>
                </td>
              </tr>
  
              <tr>
                <td>
                  {{
                    $t(`Components.Yield.ModalCreateYieldSettings.Label_Deductible`)
                  }}
                </td>
                <td>
                  <div class="control">
                    <label class="radio">
                      <input type="radio" v-model="mYieldSettings.Deductible" :checked="mYieldSettings.Deductible" :value="true" name="deductible" />
                      {{ $t('General.Yes') }}
                    </label>
                    <label class="radio">
                      <input type="radio" v-model="mYieldSettings.Deductible" :checked="!mYieldSettings.Deductible" :value="false" name="deductible" />
                      {{ $t('General.No') }}
                    </label>
                  </div>
                </td>
              </tr>
  
              <tr class="has-text-vertical-middle">
                <td>
                  {{
                    $t(
                      `Components.Yield.ModalCreateYieldSettings.Label_CalculationType`
                    )
                  }}
                </td>
                <td>
                  <div v-if="channel.PriceTypes.length === 1">
                    {{
                      $t(
                        `Components.Yield.ModalCreateYieldSettings.${mYieldSettings.CalculationType}`
                      )
                    }}
                  </div>
                  <div
                    v-if="
                      mYieldSettings.ChannelId > 0 &&
                      channel &&
                      channel.PriceTypes.length > 1
                    "
                    class="select"
                  >
                    <select v-model="mYieldSettings.CalculationType">
                      <option
                        v-for="(pct, index) in channel.PriceTypes"
                        :key="index"
                        :value="pct.PriceType"
                        :selected="
                          mYieldSettings.CalculationType === pct.PriceType
                        "
                      >
                        {{ calculationTypeLabels[pct.PriceType] }}
                      </option>
                    </select>
                  </div>
                </td>
              </tr>
  
              <tr
                v-if="mYieldSettings.CalculationType !== 'PerHourPerSeat'"
                class="has-text-vertical-middle"
              >
                <td>
                  {{
                    $t(
                      `Components.Yield.ModalCreateYieldSettings.Label_NrOfHoursPerDayPart`
                    )
                  }}
                </td>
                <td>
                  <div class="select">
                  <select
                    v-model="mYieldSettings.HoursPerDayPart"
                    @change="setPriceParts"
                  >
                    <option
                      v-for="dayPart in dayParts"
                      :key="dayPart"
                      :value="dayPart"
                      :selected="dayPart === mYieldSettings.HoursPerDayPart"
                    >
                      {{ dayPart }}
                    </option>
                  </select>
                  </div>
                </td>
              </tr>
  
              <tr class="has-text-vertical-middle">
                <td>{{ $t('Prices.SpacePriceCapMin') }}</td>
                <td>
                  <ul class="tabs is-toggle is-inline-flex is-small m-0">
                    <li :class="{
                        'is-active': mYieldSettings.SpacePriceCapMin === 0,
                      }">
                      <a @click="mYieldSettings.SpacePriceCapMin = 0">
                        <span>{{ $t(`General.State_Off`) }}</span>
                      </a>
                    </li>
                    <li :class="{
                        'is-active': mYieldSettings.SpacePriceCapMin > 0,
                      }">
                      <a @click="mYieldSettings.SpacePriceCapMin = 100">
                        <span>{{ $t(`General.State_On`) }}</span>
                      </a>
                    </li>
                  </ul>
  
                  <div
                    v-if="mYieldSettings.SpacePriceCapMin > 0"
                    class="field has-addons is-pulled-right"
                  >
                    <p class="control">
                      <a class="button is-static">&euro;</a>
                    </p>
                    <p class="control">
                      <InputNumeric 
                        :allowNegativeValue="false" 
                        v-model.number="mYieldSettings.SpacePriceCapMin" 
                      />
                    </p>
                  </div>
                </td>
              </tr>
  
              <tr class="has-text-vertical-middle">
                <td>{{ $t('Prices.SpacePriceCapMax') }}</td>
                <td>
                  <ul class="tabs is-toggle is-inline-flex is-small m-0">
                    <li :class="{
                        'is-active': mYieldSettings.SpacePriceCapMax === 0,
                      }">
                      <a @click="mYieldSettings.SpacePriceCapMax = 0">
                        <span>{{ $t(`General.State_Off`) }}</span>
                      </a>
                    </li>
                    <li :class="{
                        'is-active': mYieldSettings.SpacePriceCapMax > 0,
                      }">
                      <a @click="mYieldSettings.SpacePriceCapMax = 1000">
                        <span>{{ $t(`General.State_On`) }}</span>
                      </a>
                    </li>
                  </ul>
  
                  <div
                    v-if="mYieldSettings.SpacePriceCapMax > 0"
                    class="field has-addons is-pulled-right"
                  >
                    <p class="control">
                      <a class="button is-static">&euro;</a>
                    </p>
                    <p class="control">
                      <InputNumeric
                        :allowNegativeValue="false"
                        v-model.number="mYieldSettings.SpacePriceCapMax"
                      />
                    </p>
                  </div>
                </td>
              </tr>
  
              <tr class="has-text-vertical-middle">
                <td>{{ $t('Components.Yield.ModalCreateYieldSettings.MinPeople') }}</td>
                <td>
                  <ul class="tabs is-toggle is-inline-flex is-small m-0">
                    <li :class="{
                        'is-active': mYieldSettings.MinPeople === 0,
                      }">
                      <a @click="mYieldSettings.MinPeople = 0">
                        <span>{{ $t(`General.State_Off`) }}</span>
                      </a>
                    </li>
                    <li :class="{
                        'is-active': mYieldSettings.MinPeople > 0,
                      }">
                      <a @click="mYieldSettings.MinPeople = 1">
                        <span>{{ $t(`General.State_On`) }}</span>
                      </a>
                    </li>
                  </ul>
  
                  <div
                    v-if="mYieldSettings.MinPeople > 0"
                    class="field has-addons is-pulled-right"
                  >
                    <p class="control">
                      <InputNumeric
                        :allowNegativeValue="false"
                        v-model.number="mYieldSettings.MinPeople"
                      />
                    </p>
                  </div>
                </td>
              </tr>
              <tr class="has-text-vertical-middle">
                <td>{{ $t('Components.Yield.ModalCreateYieldSettings.MaxPeople') }}</td>
                <td>
                  <ul class="tabs is-toggle is-inline-flex is-small m-0">
                    <li :class="{
                        'is-active': mYieldSettings.MaxPeople === 0,
                      }">
                      <a @click="mYieldSettings.MaxPeople = 0">
                        <span>{{ $t(`General.State_Off`) }}</span>
                      </a>
                    </li>
                    <li :class="{
                        'is-active': mYieldSettings.MaxPeople > 0,
                      }">
                      <a @click="mYieldSettings.MaxPeople = 100">
                        <span>{{ $t(`General.State_On`) }}</span>
                      </a>
                    </li>
                  </ul>
  
                  <div
                    v-if="mYieldSettings.MaxPeople > 0"
                    class="field has-addons is-pulled-right"
                  >
                    <p class="control">
                      <InputNumeric
                        :allowNegativeValue="false"
                        v-model.number="mYieldSettings.MaxPeople"
                      />
                    </p>
                  </div>
                </td>
              </tr>
  
              <tr>
                <td>
                  {{
                    $t(`Components.Yield.ModalCreateYieldSettings.Label_Prices`)
                  }}
                </td>
                <td>
                  <div class="box">
                    <div
                      v-if="mYieldSettings.PriceParts.length === 0"
                      class="has-margin-bottom"
                    >
                      <Message>
                        {{
                          $t(
                            `Components.Yield.EditYieldSettings.Message_NoPricePartAdded`
                          )
                        }}
                      </Message>
                    </div>
                    <table class="table is-fullwidth is-striped">
                      <tbody>
                        <tr
                          v-for="(pricePart, index) in mYieldSettings.PriceParts"
                          :key="index"
                          class="has-text-vertical-middle"
                        >
                          <td>
                            <span v-if="
                                mYieldSettings.CalculationType ===
                                'PerHourPerSeat'
                              ">{{
                                $t(
                                  `Components.Yield.ModalCreateYieldSettings.Label_Hour`
                                )
                              }}
                            </span>
                            <span>{{ pricePart.Part }}</span>
                            <span v-if="
                                mYieldSettings.CalculationType !==
                                'PerHourPerSeat'
                              ">
                              {{
                                $tc(
                                  `Components.Yield.ModalCreateYieldSettings.Label_DayParts`,
                                  pricePart.Part
                                )
                              }}</span>
                          </td>
                          <td>
                            <div class="is-flex is-align-items-center">
                              <div class="field-price field has-addons is-inline-flex is-marginless">
                                <p class="control">
                                  <a
                                    class="button is-static"
                                    v-html="locationState.CurrencySymbol"
                                  ></a>
                                </p>
                                <p class="control">
                                  <InputNumeric
                                    :allowNegativeValue="false"
                                    v-model.number="pricePart.Price"
                                  />
                                </p>
                              </div>
                              <a
                                @click="removePricePart(index)"
                                class="has-text-danger has-margin-left"
                              >
                                <span class="icon">
                                  <font-awesome-icon :icon="['fas', 'trash-alt']" />
                                </span>
                              </a>
                            </div>
                          </td>
                        </tr>
                      </tbody>
  
                      <!-- Toevoegen -->
                      <tfoot>
                        <tr class="has-text-vertical-middle">
                          <td>
                            <span v-if="
                                mYieldSettings.CalculationType ===
                                'PerHourPerSeat'
                              ">{{
                                $t(
                                  `Components.Yield.ModalCreateYieldSettings.Label_Hour`
                                )
                              }}
                            </span>
                            <span>{{
                              mYieldSettings.PriceParts.length + 1
                            }}</span>
                            <span v-if="
                                mYieldSettings.CalculationType !==
                                'PerHourPerSeat'
                              ">
                              {{
                                $tc(
                                  `Components.Yield.ModalCreateYieldSettings.Label_DayParts`,
                                  mYieldSettings.PriceParts.length
                                )
                              }}</span>
                          </td>
                          <td>
                            <div class="is-inline">
                              <div class="field-price is-inline-flex field has-addons is-marginless">
                                <p class="control">
                                  <a
                                    class="button is-static"
                                    v-html="locationState.CurrencySymbol"
                                  ></a>
                                </p>
                                <p class="control">
                                  <InputNumeric
                                    :allowNegativeValue="false"
                                    v-model.number="newPricePart"
                                  />
                                </p>
                              </div>
                            </div>
                            <a
                              @click="addPricePart"
                              class="button has-margin-left is-success"
                            >
                              <span class="icon">
                                <font-awesome-icon :icon="['fas', 'plus']" />
                              </span>
                              <span v-text="$t('Form.Control.Add')"></span>
                            </a>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </ui-component-modal>
  </template>
  
  <script>
  import Vue from 'vue'
  import { mapMutations, mapState } from 'vuex'
  import channelProvider from '@/providers/channel'
  import yieldProvider from '@/providers/yield'
  import spaceProvider from '@/providers/space'
  import BaseDateField from '@/components/UI/Form/BaseDateField'
  import InputNumeric from '@/components/UI/Form/InputNumeric'
  
  const Message = () =>
    import(
      /* webpackChunkName: "settings-spaces-list" */
      '@/components/UI/Message'
    )
  
  export default {
    components: {
      BaseDateField,
      InputNumeric,
      Message,
    },
    props: {
      showModal: {
        type: Boolean,
        default: false,
      },
  
      onClickCancel: {
        type: Function,
        required: true,
      },
  
      showValidFromRow: {
        type: Boolean,
        default: true,
      },
  
      showStatusRow: {
        type: Boolean,
        default: true,
      },
  
      voucherId: {
        Type: Number,
        default: 0,
      },
  
      channelId: {
        Type: Number,
        default: 0,
      },
  
      meetingtypeId: {
        Type: Number,
        default: 0,
      },
  
      spaceId: {
        Type: Number,
        default: 0,
      },
    },
  
    data() {
      return {
        isSaving: false,
        isSavingSuccess: false,
        isSavingError: false,
        isLoadingSpaces: false,
        locationId: Number(this.$route.params.locationId),
        channel: null,
        dataFormat: 'D MMMM YYYY',
        startDate: new Date(),
        dayParts: [1, 2, 3, 4, 5, 6, 8, 12],
        mYieldSettings: null,
        priceCalculationTypes: [],
        meetingtypes: [],
        spaces: [],
        statusTypes: ['Draft', 'Approved'],
        newPricePart: 0,
        calculationTypeLabels: {
          PerHourPerSeat: this.$t(
            `Components.Yield.ModalCreateYieldSettings.Label_PricePHourPSeat`
          ),
          PerDayPartOnly: this.$t(
            `Components.Yield.ModalCreateYieldSettings.Label_PricePDayPart`
          ),
          PerDayPartPerPerson: this.$t(
            `Components.Yield.ModalCreateYieldSettings.Label_PricePDayPartPPerson`
          ),
        },
      }
    },
  
    computed: {
      ...mapState('locationStore', ['locationState']),
      ...mapState('spaceStore', ['space']),
      ...mapState('voucherStore', ['voucher']),
      ...mapState('yieldStore', ['yieldSettingsState']),
  
      taxPercentages() {
        let result = []
  
        if (this.locationState) {
          result = this.locationState.TaxPercentages.map((t) => t.TaxPercentage)
        }
  
        return result
      },
  
      channelName() {
        if (this.locationState) {
          let channelIndex = this.locationState.Channels.findIndex(
            (c) => c.ChannelId === this.channelId
          )
          if (channelIndex > -1) {
            return this.locationState.Channels[channelIndex].ChannelName
          }
        }
        return ''
      },
    },
  
    created() {
      this.mYieldSettings = JSON.parse(
        JSON.stringify(this.yieldSettingsState.yieldSettings)
      )
  
      if (this.voucherId === 0 && this.spaceId === 0) {
        this.getLocationSpaces()
      }
  
      this.startDate = this.mYieldSettings.ValidFrom
  
      if (this.mYieldSettings.ChannelId > 0) {
        this.getChannelData()
      }
  
      if (!this.channel) {
        let defaultChannelId = this.locationState.Settings.ChannelId
        let channelIndex = this.locationState.Channels.findIndex(
          (lc) => lc.ChannelId === defaultChannelId
        )
        if (channelIndex > -1) {
          this.channel = this.locationState.Channels[channelIndex]
        }
      }
    },
  
    methods: {
      ...mapMutations('yieldStore', ['setYieldSettingsState']),
  
      getLocationSpaces() {
        let self = this
  
        if (!self.isLoadingSpaces) {
          self.isLoadingSpaces = true
  
          spaceProvider.methods.getLocationSpaces(self.locationId, '', 0, 0, 0, 100)
            .then((response) => {
              if (response.status === 200) {
                self.spaces = response.data.Results
              }
            })
            .finally(() => {
              self.isLoadingSpaces = false
            })
        }
      },
  
      getChannelData() {
        channelProvider.methods
          .getChannelById(this.mYieldSettings.ChannelId)
          .then((response) => {
            if (response.status === 200) {
              this.channel = response.data
            }
          })
      },
  
      setPriceParts() {
        // this.mYieldSettings.PriceParts = []
        // let nrOfParts = 12 / this.mYieldSettings.HoursPerDayPart
  
        // for (let i = 0; i < nrOfParts; i++) {
        //   this.mYieldSettings.PriceParts.push({
        //     ChannelId: this.mYieldSettings.ChannelId,
        //     LocationId: this.mYieldSettings.LocationId,
        //     MeetingtypeId: this.mYieldSettings.MeetingtypeId,
        //     SpaceId: this.mYieldSettings.SpaceId,
        //     Part: i + 1,
        //     Price: 0,
        //   })
        // }
      },
  
      addPricePart() {
        let existingPriceParts = this.mYieldSettings.PriceParts.length
        this.mYieldSettings.PriceParts.push({
          ChannelId: this.mYieldSettings.ChannelId,
          LocationId: this.mYieldSettings.LocationId,
          MeetingtypeId: this.mYieldSettings.MeetingtypeId,
          SpaceId: this.mYieldSettings.SpaceId,
          Part: existingPriceParts + 1,
          Price: this.newPricePart,
        })
  
        this.newPricePart = 0
      },
  
      removePricePart(index) {
        let self = this
  
        self.mYieldSettings.PriceParts.splice(index, 1)
  
        for (let i = 0; i < self.mYieldSettings.PriceParts.length; i++) {
          self.mYieldSettings.PriceParts[i].Part = i + 1
        }
      },
  
      saveYieldSettings() {
        let self = this
        self.isSaving = true
  
        if (this.newPricePart > 0) {
          // Add new price part when user forgot to press on the add button
          this.addPricePart()
        }
  
        self.mYieldSettings.ValidFrom =
          self.$options.filters.dateObjectIsoDateString(self.startDate)
        self.yieldSettingsState.yieldSettings = self.mYieldSettings
        yieldProvider.methods
          .updateLocationYieldSettings(self.mYieldSettings)
          .then((response) => {
            if (response.status === 200) {
              this.$emit('reload')
  
              self.isSavingSuccess = true
              self.setYieldSettingsState(self.yieldSettingsState)
  
              setTimeout(() => {
                self.onClickCancel()
              }, 2000)
            }
          })
          .catch((error) => {
            self.isSavingError = true
          })
          .finally(() => {
            self.isSaving = false
          })
      },
    },
  }
  </script>
  
  <style lang="scss" scoped>
  .table-settings {
    > tbody > tr > td:first-child {
      width: 210px;
    }
  }
  .customWidth {
    display: inline-flex !important;
    width: auto;
  }
  </style>
  